import React from "react";
import { useTheme } from "./theme-provider";
import svgPaths from "../../imports/svg-x918lea3n5";

type Wrapper3Props = {
  additionalClassNames?: string;
};

function Wrapper3({ children, additionalClassNames = "" }: React.PropsWithChildren<Wrapper3Props>) {
  return (
    <div className={`content-stretch flex flex-col relative shrink-0 w-full ${additionalClassNames}`}>
      <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">{children}</div>
    </div>
  );
}

function Wrapper2({ children }: React.PropsWithChildren<{}>) {
  return (
    <div className="relative shrink-0 size-[28px]">
      <svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 28 28">
        {children}
      </svg>
    </div>
  );
}

type FooterVector2Props = {
  additionalClassNames?: string;
};

function FooterVector2({ children, additionalClassNames = "" }: React.PropsWithChildren<FooterVector2Props>) {
  return (
    <div className={`absolute bottom-1/2 top-1/2 ${additionalClassNames}`}>
      <div className="absolute inset-[-1px_-50%]">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 4 2">
          {children}
        </svg>
      </div>
    </div>
  );
}

type Wrapper1Props = {
  additionalClassNames?: string;
};

function Wrapper1({ children, additionalClassNames = "" }: React.PropsWithChildren<Wrapper1Props>) {
  return (
    <div className={additionalClassNames}>
      <div className="absolute inset-[-100%]">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 3 3">
          {children}
        </svg>
      </div>
    </div>
  );
}

type WrapperProps = {
  additionalClassNames?: string;
};

function Wrapper({ children, additionalClassNames = "" }: React.PropsWithChildren<WrapperProps>) {
  return <Wrapper1 additionalClassNames={`absolute bottom-3/4 top-[20.83%] ${additionalClassNames}`}>{children}</Wrapper1>;
}

type FooterVector1Props = {
  additionalClassNames?: string;
};

function FooterVector1({ children, additionalClassNames = "" }: React.PropsWithChildren<FooterVector1Props>) {
  return <Wrapper1 additionalClassNames={`absolute bottom-[20.83%] top-3/4 ${additionalClassNames}`}>{children}</Wrapper1>;
}

type FooterVectorProps = {
  additionalClassNames?: string;
};

function FooterVector({ children, additionalClassNames = "" }: React.PropsWithChildren<FooterVectorProps>) {
  return (
    <div className={`absolute left-1/2 right-1/2 ${additionalClassNames}`}>
      <div className="absolute inset-[-50%_-1px]">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 4">
          {children}
        </svg>
      </div>
    </div>
  );
}

function FooterFrame({ children }: React.PropsWithChildren<{}>) {
  return (
    <Wrapper2>
      <g id="Frame">{children}</g>
    </Wrapper2>
  );
}

export function BenchmarkFooter() {
  const { mode, setMode, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <div className="relative w-full" style={{ backgroundColor: 'var(--brand-bg)' }}>
      <div aria-hidden="true" className="absolute border-solid border-t-[0.571px] inset-x-0 top-0 pointer-events-none" style={{ borderColor: 'var(--brand-border-line)' }} />
      <div className="content-stretch flex flex-col gap-[48px] items-start pb-[56px] pt-[48px] px-[24px] relative w-full">
        <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
          <div className="h-[42px] relative shrink-0 w-[154.636px]" data-name="Logo">
            <svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 154.636 42">
              <path d={svgPaths.p8989200} fill={isDark ? "white" : "url(#paint0_linear_4013_1238)"} id="Logo" />
              {!isDark && (
                <defs>
                  <linearGradient gradientUnits="userSpaceOnUse" id="paint0_linear_4013_1238" x1="77.3179" x2="77.3179" y1="42" y2="0">
                    <stop stopColor="#340825" />
                    <stop offset="1" stopColor="#AB704B" />
                  </linearGradient>
                </defs>
              )}
            </svg>
          </div>
        </div>
        <Wrapper3 additionalClassNames="flex-col md:flex-row gap-[24px] md:gap-0 items-start md:items-center">
          <div className="content-stretch flex font-['Helvetica_Neue:Regular',sans-serif] flex-wrap gap-x-[32px] md:gap-x-[48px] gap-y-[12px] items-center leading-[20px] not-italic relative shrink-0 text-[16px]" style={{ color: 'var(--brand-text)' }}>
            <p className="opacity-60 relative shrink-0">Research Grants</p>
            <p className="opacity-60 relative shrink-0 text-center">All Competitions</p>
            <p className="opacity-60 relative shrink-0 text-center">Playground</p>
            <p className="opacity-60 relative shrink-0 text-center">Events</p>
            <p className="opacity-60 relative shrink-0">Blog</p>
          </div>
          <div className="content-stretch flex gap-[28px] items-center justify-start md:justify-end relative shrink-0 w-full md:w-auto md:flex-1">
            <Wrapper2>
              <g id="youtube">
                <path d={svgPaths.p1d139100} fill={isDark ? "white" : "#3B0E27"} id="Subtract" />
              </g>
            </Wrapper2>
            <FooterFrame>
              <path d={svgPaths.p206d78c0} fill={isDark ? "white" : "#3B0E27"} id="Vector" />
            </FooterFrame>
            <Wrapper2>
              <g id="X">
                <path d={svgPaths.p359477c0} fill={isDark ? "white" : "#3B0E27"} id="Vector" />
              </g>
            </Wrapper2>
            <Wrapper2>
              <g id="tiktok">
                <path d={svgPaths.p20274100} fill={isDark ? "white" : "#3B0E27"} id="Vector" opacity="0.5" />
                <path d={svgPaths.p3f518400} fill={isDark ? "white" : "#3B0E27"} id="Vector_2" />
                <path d={svgPaths.p2e4722b0} fill={isDark ? "white" : "#3B0E27"} id="Vector_3" />
                <path d={svgPaths.p1add27f0} fill={isDark ? "white" : "#3B0E27"} id="Vector_4" />
                <path d={svgPaths.p2adb8100} fill={isDark ? "white" : "#3B0E27"} id="Vector_5" opacity="0.5" />
              </g>
            </Wrapper2>
            <Wrapper2>
              <g id="Bilibili">
                <path clipRule="evenodd" d={svgPaths.p78ce580} fill={isDark ? "white" : "#3B0E27"} fillRule="evenodd" id="Vector" />
              </g>
            </Wrapper2>
            <FooterFrame>
              <path d={svgPaths.pc590500} fill={isDark ? "white" : "#340825"} id="Vector" />
            </FooterFrame>
            <div className="overflow-clip relative shrink-0 size-[28px]" data-name="Zulip">
              <div className="absolute inset-[10.71%_16.54%_10.71%_13.16%]" data-name="Group">
                <svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 19.6853 22">
                  <g id="Group">
                    <path d={svgPaths.p2fe22400} fill={isDark ? "white" : "#3B0E27"} id="Vector" />
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </Wrapper3>
        <Wrapper3 additionalClassNames="flex-col md:flex-row items-start md:items-end justify-start md:justify-center gap-[24px]">
          <div className="content-stretch flex flex-1 gap-[24px] items-center min-h-px min-w-px relative order-1 md:order-none">
            {/* System / Monitor */}
            <button
              onClick={() => setMode("system")}
              className="bg-transparent border-none cursor-pointer p-0"
              title="Follow system"
            >
              <div className="overflow-clip relative shrink-0 size-[24px]" style={{ opacity: mode === 'system' ? 1 : 0.54 }}>
                <div className="absolute inset-[16.67%_8.33%_29.17%_8.33%]" data-name="Vector">
                  <div className="absolute inset-[-7.69%_-5%]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 22 15">
                      <path d={svgPaths.p209ef9c0} id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
                <div className="absolute inset-[87.5%_33.33%_12.5%_33.33%]" data-name="Vector">
                  <div className="absolute inset-[-1px_-12.5%]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 10 2">
                      <path d="M1 1H9" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
                <div className="absolute bottom-[12.5%] left-1/2 right-1/2 top-3/4" data-name="Vector">
                  <div className="absolute inset-[-33.33%_-1px]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 5">
                      <path d="M1 1V4" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>
            {/* Light / Sun */}
            <button
              onClick={() => setMode("light")}
              className="bg-transparent border-none cursor-pointer p-0"
              title="Light mode"
            >
              <div className="overflow-clip relative shrink-0 size-[24px]" style={{ opacity: mode === 'light' ? 1 : 0.54 }}>
                <div className="absolute inset-[29.17%]" data-name="Vector">
                  <div className="absolute inset-[-10%]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                      <path d={svgPaths.p3e7757b0} id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
                <FooterVector additionalClassNames="bottom-[83.33%] top-[8.33%]">
                  <path d="M1 1V3" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector>
                <FooterVector additionalClassNames="bottom-[8.33%] top-[83.33%]">
                  <path d="M1 1V3" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector>
                <Wrapper additionalClassNames="left-[20.83%] right-3/4">
                  <path d="M1 1L2 2" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </Wrapper>
                <FooterVector1 additionalClassNames="left-3/4 right-[20.83%]">
                  <path d="M1 1L2 2" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector1>
                <FooterVector2 additionalClassNames="left-[8.33%] right-[83.33%]">
                  <path d="M1 1H3" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector2>
                <FooterVector2 additionalClassNames="left-[83.33%] right-[8.33%]">
                  <path d="M1 1H3" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector2>
                <FooterVector1 additionalClassNames="left-[20.83%] right-3/4">
                  <path d="M1 2L2 1" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </FooterVector1>
                <Wrapper additionalClassNames="left-3/4 right-[20.83%]">
                  <path d="M1 2L2 1" id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </Wrapper>
              </div>
            </button>
            {/* Dark / Moon */}
            <button
              onClick={() => setMode("dark")}
              className="bg-transparent border-none cursor-pointer p-0"
              title="Dark mode"
            >
              <div className="relative shrink-0 size-[24px]" data-name="Weather / Moon">
                <svg className="absolute block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
                  <g id="Weather / Moon" opacity={mode === 'dark' ? 1 : 0.54}>
                    <path d={svgPaths.p3e7e2d00} id="Vector" stroke={isDark ? "white" : "#3B0E27"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                  </g>
                </svg>
              </div>
            </button>
          </div>
          <p className="flex-1 font-['Arial:Regular',sans-serif] leading-[20px] min-h-px min-w-px not-italic opacity-60 relative text-[14px] text-left md:text-right order-2 md:order-none" style={{ color: 'var(--brand-text-alt)' }}>© 2026 SAIR. All rights reserved.</p>
        </Wrapper3>
      </div>
    </div>
  );
}
